import { AnchorMark, ArchMark, ThermalMark, LeisureMark } from "@/components/marks";
import { BackButton, SectionLabel, ColorSwatch, TypographyCard, MarkShowcase } from "@/components/shared";
import { COLOR_PALETTE } from "@/lib/constants";

export function BrandPage() {
  return (
    <div className="pt-40 pb-24 px-6 md:px-12 max-w-7xl mx-auto animate-in fade-in duration-700">
      <BackButton />

      <div className="border-b border-[#293129]/20 pb-12 mb-16">
        <h1 className="font-serif text-5xl md:text-7xl uppercase mb-6">Brand Identity<br /><span className="text-[#1D4C74] italic">& Style Guide.</span></h1>
        <p className="tracking-widest uppercase text-xs leading-loose opacity-80 max-w-2xl">
          This document governs the visual and tonal execution of the Miracle Hill Leisure Club. We do not use "woo-woo" spirituality. We ground the metaphysical in the physical: sun-bleached adobe, geometric tile, vintage leisure, and precise science.
        </p>
      </div>

      {/* 01 — COLOR PALETTE */}
      <div className="mb-24">
        <SectionLabel number="01" label="COLOR PALETTE" className="mb-8" />
        <p className="tracking-widest uppercase text-xs leading-loose opacity-80 mb-8 max-w-xl">
          The palette rejects the sterile whites and aquatic blues of the traditional spa industry. Our colors are drawn from the physical materials of the desert compound.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {COLOR_PALETTE.map((color) => (
            <ColorSwatch
              key={color.hex}
              name={color.name}
              hex={color.hex}
              description={color.desc}
            />
          ))}
        </div>
      </div>

      {/* 02 — TYPOGRAPHY */}
      <div className="mb-24 border-t border-[#293129]/20 pt-16">
        <SectionLabel number="02" label="TYPOGRAPHY" className="mb-8" />
        <p className="tracking-widest uppercase text-xs leading-loose opacity-80 mb-12 max-w-xl">
          Our typographic contrast mirrors our philosophy: elegant heritage meeting rigid, geometric modernism.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <TypographyCard
            label="Primary Serif / Headlines"
            description="Used for massive, poetic statements, article titles, and editorial emphasis. Frequently italicized to represent fluid energy."
          >
            <h3 className="font-serif text-6xl mb-4">Cormorant Garamond</h3>
            <p className="font-serif italic text-4xl mb-8 text-[#1D4C74]">Playful Sophistication.</p>
            <div className="flex items-end justify-between opacity-30 font-serif">
              <span className="text-8xl leading-none">Aa</span>
              <span className="text-2xl tracking-widest">0123456789</span>
            </div>
          </TypographyCard>

          <TypographyCard
            label="Primary Sans / Body & UI"
            description="Used for paragraphs, navigation, and metadata. By forcing body copy into all-caps with 0.2em+ tracking, we slow the reader down, treating text as architectural geometry."
          >
            <h3 className="font-sans text-3xl font-bold tracking-widest uppercase mb-6 mt-3">Montserrat</h3>
            <p className="font-sans text-xs tracking-[0.3em] uppercase mb-8 leading-loose font-bold text-[#C06B5D]">Strictly all caps with heavy tracking to form rigid blocks.</p>
            <div className="flex items-end justify-between opacity-30 font-sans font-bold">
              <span className="text-7xl leading-none">Aa</span>
              <span className="text-xl tracking-[0.2em]">0123456789</span>
            </div>
          </TypographyCard>
        </div>
      </div>

      {/* 03 — THE FLUID LEXICON */}
      <div className="border-t border-[#293129]/20 pt-16">
        <SectionLabel number="03" label="THE FLUID LEXICON" className="mb-8" />
        <p className="tracking-widest uppercase text-xs leading-loose opacity-80 mb-12 max-w-xl">
          We do not have a single logo. We have a lexicon of marks that adapt to the medium they live on—from digital screens to carved stone.
        </p>

        <div className="space-y-8">
          <MarkShowcase
            mark={<AnchorMark className="w-full" />}
            bgColor="#1D4C74"
            textColor="#ECE9E0"
            name="The Editorial Anchor"
            usage="Usage: Digital Headers, Official Signage, Press"
            description="The primary voice of the brand. It grounds the esoteric elements in physical geography (Desert Hot Springs) and clearly states our positioning (Leisure Club). Must always be displayed with high contrast."
          />

          <MarkShowcase
            mark={<ArchMark className="w-32 h-32" />}
            bgColor="#C06B5D"
            textColor="#ECE9E0"
            name="The Architect's Arch"
            usage="Usage: Etched Glass, Blueprints, Secondary Icons"
            description="Represents the signature adobe architecture framing the desert sun and thermal pools. Rendered in clean, unvarying stroke weights. Never distort the geometry."
            className="arch-crop md:rounded-none"
          />

          <MarkShowcase
            mark={<ThermalMark className="w-32 h-32" />}
            bgColor="#ECE9E0"
            textColor="#293129"
            name="The Citizen Scientist"
            usage="Usage: Watermarks, Almanac Covers, Technical Specs"
            description="An abstract topographical map representing the invisible vortex currents and thermal fault lines researched by the original Eccentric Host. Use when discussing geology or metaphysics."
          />

          <MarkShowcase
            mark={<LeisureMark className="w-32 h-32" />}
            bgColor="#8F9B82"
            textColor="#ECE9E0"
            name="The Leisure Seal"
            usage="Usage: Pool Towels, Coasters, Matchbooks"
            description="A playful nod to classic 1970s cabana stripes. This seal injects the &quot;playful sophistication&quot; into the brand. Used strictly for physical leisure goods and hospitality touchpoints."
          />
        </div>
      </div>
    </div>
  );
}
